import type { ReactNode } from "react";
import { CheckCircle2, AlertTriangle, Clock, HelpCircle } from "lucide-react";
import type { LegalityResult, LegalityState } from "@/lib/types";
import { t } from "@/lib/i18n";

/**
 * Simplified legality badge.
 *
 * Two main states: "Erlaubt" (green) or "Verboten" (red/amber).
 * A small footer disclaimer is always shown — never the verbose
 * repeated "ohne Gewähr" text per state.
 */
export interface LegalityBadgeProps {
  state: LegalityState;
  result?: LegalityResult | null;
  loading?: boolean;
  variant?: "pill" | "card";
}

const STATE_STYLES: Record<
  LegalityState,
  { dot: string; bg: string; ring: string; icon: ReactNode }
> = {
  green: {
    dot: "bg-legal-green",
    bg: "bg-legal-green/10 text-legal-green",
    ring: "ring-legal-green/40",
    icon: <CheckCircle2 size={16} />,
  },
  amber: {
    dot: "bg-legal-amber",
    bg: "bg-legal-amber/10 text-legal-amber",
    ring: "ring-legal-amber/40",
    icon: <Clock size={16} />,
  },
  red: {
    dot: "bg-legal-red",
    bg: "bg-legal-red/10 text-legal-red",
    ring: "ring-legal-red/40",
    icon: <AlertTriangle size={16} />,
  },
  grey: {
    dot: "bg-legal-grey",
    bg: "bg-legal-grey/10 text-legal-grey",
    ring: "ring-legal-grey/40",
    icon: <HelpCircle size={16} />,
  },
};

export function LegalityBadge({
  state,
  result,
  loading,
  variant = "pill",
}: LegalityBadgeProps) {
  const styles = STATE_STYLES[state];

  const label = loading
    ? t("legality.loading")
    : state === "red"
      ? t("legality.red.label")
      : state === "amber"
        ? t("legality.amber.label")
        : state === "grey"
          ? t("legality.grey.label")
          : t("legality.green.label");

  // ---- Compact pill ----
  if (variant === "pill") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${styles.bg} ${styles.ring}`}
        role="status"
      >
        <span className={`inline-block h-2 w-2 rounded-full ${styles.dot}`} />
        {label}
      </span>
    );
  }

  // ---- Full card (create flow) ----
  let detail = label;
  let subtext = "";
  if (!loading && state === "red" && result) {
    const cats = (result.blockingCategories || [])
      .map((c) => t(`category.${c}`))
      .filter(Boolean)
      .join(", ");
    if (cats) {
      detail = `${t("legality.red.label")}: ${cats}`;
    }
    if (result.nearestDistanceM != null) {
      const dist =
        result.nearestDistanceM >= 1000
          ? `${(result.nearestDistanceM / 1000).toFixed(1)} ${t("legality.kmUnit")}`
          : `${Math.round(result.nearestDistanceM)} ${t("legality.distanceUnit")}`;
      subtext = t("legality.red.nearest", { distance: dist });
    }
  }

  return (
    <div
      className={`rounded-xl p-3 ring-1 ${styles.bg} ${styles.ring} fade-in`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        {styles.icon}
        <span className="font-semibold">{detail}</span>
      </div>
      {subtext ? (
        <p className="mt-1 text-sm opacity-90">{subtext}</p>
      ) : null}
      {/* Single small footer disclaimer — always shown */}
      {!loading ? (
        <p className="mt-2 text-xs text-fg-subtle">{t("legality.footer")}</p>
      ) : null}
    </div>
  );
}
